import { useEffect, useRef } from "react";
// 3rd parties
import L from "leaflet";
import moment from "moment";

// owner
import CommonMapReact from "@containers/CommonMapReact/CommonMapReact";
import TowerIcon from "@images/map/Cell_tower.svg";
import WifiIcon from "@images/map/Wi-Fi.svg";
import StopIcon from "@images/map/stop.svg";

import styled from "styled-components";
import MarkerIcon from "@images/mapbox/images/marker-icon.png";

const Style = styled.div`
  .tower-locationiq {
    filter: brightness(1.2) saturate(2) hue-rotate(180deg)
      drop-shadow(0 0 2px #2196f3);
  }
  .tower-opencellid {
    filter: brightness(1.5) saturate(3) hue-rotate(10deg)
      drop-shadow(0 0 2px #ffeb3b);
  }
  .tower-here {
    filter: brightness(1.2) saturate(2) hue-rotate(80deg)
      drop-shadow(0 0 2px #4caf50);
  }
  ul {
    padding-left: 1rem;
  }
`;

function UpdateWifiCellularMismatchMap({
  items,
  onClick,
  showWifi,
  showLocationiq,
  showOpencell,
  showHere,
  showAdjacentTrustedPoints,
}) {
  const map = useRef();
  const wifiHull = useRef();
  const marker = useRef();
  const wifiLayer = useRef();
  const locationiqLayer = useRef();
  const opencellLayer = useRef();
  const hereLayer = useRef();
  const trustedPointsLayer = useRef();
  const onClickRef = useRef(onClick);

  // Keep onClick ref updated
  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  // Effect for setting up map data and markers (only when items change)
  useEffect(() => {
    const mapInstance = map.current;
    if (!items || items.length === 0) return;

    // Remove previous data layers only (preserve user-placed marker)
    if (wifiLayer.current) {
      mapInstance.removeLayer(wifiLayer.current);
    }
    if (locationiqLayer.current) {
      mapInstance.removeLayer(locationiqLayer.current);
    }
    if (opencellLayer.current) {
      mapInstance.removeLayer(opencellLayer.current);
    }
    if (hereLayer.current) {
      mapInstance.removeLayer(hereLayer.current);
    }
    if (trustedPointsLayer.current) {
      mapInstance.removeLayer(trustedPointsLayer.current);
    }
    if (wifiHull.current) {
      mapInstance.removeLayer(wifiHull.current);
    }

    // DO NOT remove marker.current - preserve user-selected location

    wifiLayer.current = L.layerGroup();
    locationiqLayer.current = L.layerGroup();
    opencellLayer.current = L.layerGroup();
    hereLayer.current = L.layerGroup();
    trustedPointsLayer.current = L.layerGroup();

    let bounds = L.latLngBounds();
    const allCoordinates = [];

    for (let i = 0; i < items.length; i++) {
      let item = items[i];
      if (item.wifiHull && item.wifiHull.length > 1) {
        allCoordinates.push(...item.wifiHull.map((c) => [c.x, c.y]));
      }

      // WiFi marker
      if (item.wifiLat && item.wifiLng) {
        const wifiMarker = new L.marker([item.wifiLat, item.wifiLng], {
          icon: L.icon({ iconUrl: WifiIcon, iconAnchor: [23, 35] }),
        });
        wifiMarker.addTo(wifiLayer.current);
        bounds.extend(wifiMarker.getLatLng());
      }

      // Tower marker
      if (item.cellLat && item.cellLng) {
        let className = "";
        let targetLayer = null;

        if (item.wifiProvider === "locationIQ") {
          className = "tower-locationiq";
          targetLayer = locationiqLayer.current;
        } else if (item.wifiProvider === "openCellId") {
          className = "tower-opencellid";
          targetLayer = opencellLayer.current;
        } else if (item.wifiProvider === "here") {
          className = "tower-here";
          targetLayer = hereLayer.current;
        }

        if (targetLayer) {
          const towerMarker = new L.marker([item.cellLat, item.cellLng], {
            icon: L.icon({
              iconUrl: TowerIcon,
              iconAnchor: [23, 35],
              className,
            }),
          }).bindTooltip("(" + item.wifiProvider + ")");
          towerMarker.addTo(targetLayer);
          bounds.extend(towerMarker.getLatLng());
        }
      }
    }

    // Add trusted points markers
    const trustedPoints = items.flatMap((item) =>
      (item.trustedLocations || []).map((tp) => ({
        latitude: tp.lat,
        longitude: tp.lng,
        locationSource: tp.source,
        createdAt: tp.timestamp,
        deviceSerial: item.device?.serial,
      }))
    );

    trustedPoints.forEach((point) => {
      if (point.latitude && point.longitude) {
        const trustedMarker = new L.marker([point.latitude, point.longitude], {
          icon: L.icon({
            iconUrl: StopIcon,
            iconAnchor: [12, 12],
          }),
        }).bindTooltip(
          `Source: (${point.locationSource})<br/>` +
            `Device: ${point.deviceSerial}<br/>` +
            `Time: ${moment(point.createdAt).format("lll")}`
        );
        trustedMarker.addTo(trustedPointsLayer.current);
        bounds.extend(trustedMarker.getLatLng());
      }
    });

    // Create a single polygon with all coordinates
    if (allCoordinates.length > 0) {
      wifiHull.current = L.polygon(allCoordinates, {
        fillOpacity: 0.3,
        weight: 1,
        opacity: 0.3,
      }).addTo(mapInstance);
      bounds.extend(wifiHull.current.getBounds());
    }

    if (bounds.isValid()) {
      mapInstance.fitBounds(bounds, { animate: true, maxZoom: 15 });
    }

    // Set up map click handler (only once when items change)
    const handleMapClick = function (event) {
      // Remove only the existing user-placed marker (not other layers)
      if (marker.current) {
        mapInstance.removeLayer(marker.current);
        marker.current = null;
      }

      // Create new marker with smooth animation
      const lat = parseFloat(event.latlng.lat.toFixed(6));
      const lng = parseFloat(event.latlng.lng.toFixed(6));

      marker.current = new L.marker([lat, lng], {
        icon: L.icon({
          iconUrl: MarkerIcon,
          iconAnchor: [13, 41],
        }),
        draggable: true,
        opacity: 0,
      });

      // Add marker with fade-in effect
      marker.current
        .bindTooltip(`(${lat}, ${lng})`, {
          permanent: false,
          direction: "top",
          offset: [0, -10],
        })
        .addTo(mapInstance);

      // Smooth fade-in animation
      setTimeout(() => {
        if (marker.current) {
          marker.current.setOpacity(1);
        }
      }, 50);

      // Handle drag end with debounce
      let dragTimeout;
      marker.current.on("dragend", function (dragEvent) {
        clearTimeout(dragTimeout);
        dragTimeout = setTimeout(() => {
          const newLat = parseFloat(dragEvent.target._latlng.lat.toFixed(6));
          const newLng = parseFloat(dragEvent.target._latlng.lng.toFixed(6));

          marker.current.bindTooltip(`(${newLat}, ${newLng})`, {
            permanent: false,
            direction: "top",
            offset: [0, -10],
          });

          onClickRef.current &&
            onClickRef.current({
              latitude: newLat,
              longitude: newLng,
            });
        }, 100);
      });

      // Call onClick with debounce
      let clickTimeout;
      clearTimeout(clickTimeout);
      clickTimeout = setTimeout(() => {
        onClickRef.current &&
          onClickRef.current({
            latitude: lat,
            longitude: lng,
          });
      }, 50);
    };

    mapInstance.off("click"); // Remove any existing click handlers
    mapInstance.on("click", handleMapClick);

    // Clean up on unmount
    return () => {
      if (wifiLayer.current) mapInstance.removeLayer(wifiLayer.current);
      if (locationiqLayer.current)
        mapInstance.removeLayer(locationiqLayer.current);
      if (opencellLayer.current) mapInstance.removeLayer(opencellLayer.current);
      if (hereLayer.current) mapInstance.removeLayer(hereLayer.current);
      if (trustedPointsLayer.current)
        mapInstance.removeLayer(trustedPointsLayer.current);
      if (wifiHull.current) mapInstance.removeLayer(wifiHull.current);
      mapInstance.off("click", handleMapClick);
    };
  }, [items]); // Remove onClick from dependencies to prevent unnecessary rerenders

  // Separate effect for layer visibility (preserves marker)
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    // Remove all layers from map first
    if (wifiLayer.current && mapInstance.hasLayer(wifiLayer.current)) {
      mapInstance.removeLayer(wifiLayer.current);
    }
    if (
      locationiqLayer.current &&
      mapInstance.hasLayer(locationiqLayer.current)
    ) {
      mapInstance.removeLayer(locationiqLayer.current);
    }
    if (opencellLayer.current && mapInstance.hasLayer(opencellLayer.current)) {
      mapInstance.removeLayer(opencellLayer.current);
    }
    if (hereLayer.current && mapInstance.hasLayer(hereLayer.current)) {
      mapInstance.removeLayer(hereLayer.current);
    }
    if (
      trustedPointsLayer.current &&
      mapInstance.hasLayer(trustedPointsLayer.current)
    ) {
      mapInstance.removeLayer(trustedPointsLayer.current);
    }

    // Add layers back based on visibility settings
    if (showWifi && wifiLayer.current) {
      wifiLayer.current.addTo(mapInstance);
    }
    if (showLocationiq && locationiqLayer.current) {
      locationiqLayer.current.addTo(mapInstance);
    }
    if (showOpencell && opencellLayer.current) {
      opencellLayer.current.addTo(mapInstance);
    }
    if (showHere && hereLayer.current) {
      hereLayer.current.addTo(mapInstance);
    }
    if (showAdjacentTrustedPoints && trustedPointsLayer.current) {
      trustedPointsLayer.current.addTo(mapInstance);
    }
  }, [
    showWifi,
    showLocationiq,
    showOpencell,
    showHere,
    showAdjacentTrustedPoints,
  ]);

  return (
    <Style>
      <CommonMapReact
        mapRef={map}
        height="600px"
        options={{
          zoomPosition: "topright",
          hideCurrentLocation: true,
          hideFullScreen: true,
          showGeozoneLayer: true,
          showNetworkLayer: true,
          searchBox: true,
        }}
      />
    </Style>
  );
}

export default UpdateWifiCellularMismatchMap;
